import { dequal } from "dequal";
import { chatIdentifierToString, ChatMap, CommunityMap, emptyChatMetrics, MessageContextMap, MessageMap, nullMembership, ONE_MINUTE_MILLIS, ROLE_OWNER, SafeMap, } from "openchat-shared";
import { writable } from "../utils/stores";
import { revokeObjectUrls } from "../utils/url";
import { chatDetailsLocalUpdates } from "./chat/detailsUpdates";
import { chatSummaryLocalUpdates, ChatSummaryUpdates } from "./chat/summaryUpdates";
import { communityLocalUpdates } from "./community/detailUpdates";
import { communitySummaryLocalUpdates, CommunitySummaryUpdates } from "./community/summaryUpdates";
import { createDraftMessagesStore } from "./draft";
import { LocalChatMap, LocalCommunityMap, LocalMap } from "./map";
import { MessageLocalUpdates, messageLocalUpdates, } from "./message/localUpdates";
import { ChatLocalSet, LocalSet } from "./set";
import { scheduleUndo } from "./undo";
import { addToWritableLocalMap, addToWritableLocalSet, addToWritableMap, modifyWritable, modifyWritableMap, notEq, removeFromWritableLocalMap, removeFromWritableLocalSet, removeFromWritableMap, } from "./utils";
function emptyUnconfirmed() {
    return new Map();
}
// global local updates don't need the manager because they are not specific to a keyed entity (community, chat, message etc)
export class GlobalLocalState {
    #blockedDirectUsers = writable(new LocalSet(), undefined, notEq);
    #recentlySentMessages = writable(new SafeMap(), undefined, notEq);
    #ephemeral = writable(new MessageContextMap(), undefined, notEq);
    #unconfirmed = writable(new MessageContextMap(), undefined, notEq);
    #failedMessages = writable(new MessageContextMap(), undefined, notEq);
    draftMessages = createDraftMessagesStore();
    chats = writable(new LocalChatMap(), undefined, notEq);
    communities = writable(new LocalCommunityMap(), undefined, notEq);
    previewCommunities = writable(new CommunityMap(), undefined, notEq);
    directChatBots = writable(new LocalMap(), undefined, notEq);
    #walletConfig = writable(undefined);
    #streakInsurance = writable(undefined);
    #messageActivityFeedReadUpTo = writable(undefined);
    favourites = writable(new ChatLocalSet(), undefined, notEq);
    #uninitialisedDirectChats = writable(new ChatMap(), undefined, notEq);
    #groupChatPreviews = writable(new ChatMap(), undefined, notEq);
    // only used for testing
    clearAll() {
        this.#blockedDirectUsers.set(new LocalSet());
        this.#failedMessages.set(new MessageContextMap());
        this.#recentlySentMessages.set(new SafeMap());
        this.#ephemeral.set(new MessageContextMap());
        this.#unconfirmed.set(new MessageContextMap());
        this.chats.set(new LocalChatMap());
        this.communities.set(new LocalCommunityMap());
        this.previewCommunities.set(new CommunityMap());
        this.directChatBots.set(new LocalMap());
        this.#walletConfig.set(undefined);
        this.#streakInsurance.set(undefined);
        this.#messageActivityFeedReadUpTo.set(undefined);
        this.favourites.set(new ChatLocalSet());
        this.#uninitialisedDirectChats.set(new ChatMap());
        this.#groupChatPreviews.set(new ChatMap());
        messageLocalUpdates.set(new MessageMap());
        chatDetailsLocalUpdates.clearAll();
        chatSummaryLocalUpdates.set(new ChatMap());
        communityLocalUpdates.clear();
        communitySummaryLocalUpdates.set(new CommunityMap());
    }
    blockDirectUser(userId) {
        return addToWritableLocalSet(userId, this.#blockedDirectUsers);
    }
    unblockDirectUser(userId) {
        return removeFromWritableLocalSet(userId, this.#blockedDirectUsers);
    }
    get blockedDirectUsers() {
        return this.#blockedDirectUsers;
    }
    get unconfirmed() {
        return this.#unconfirmed;
    }
    initialiseFailedMessages(messages) {
        this.#failedMessages.set(messages);
    }
    addFailedMessage(key, message) {
        this.#failedMessages.update((map) => {
            const state = map.get(key) ?? new Map();
            state.set(message.event.messageId, message);
            return map;
        });
    }
    anyFailed(key) {
        return (this.#failedMessages.value.get(key)?.size ?? 0) > 0;
    }
    isFailed(key, messageId) {
        return this.#failedMessages.value.get(key)?.has(messageId) ?? false;
    }
    get failedMessages() {
        return this.#failedMessages;
    }
    failedMessagesForContext(key) {
        const state = this.#failedMessages.value.get(key);
        return state ? [...state.values()] : [];
    }
    deleteFailedMessage(key, messageId) {
        let deleted = false;
        this.#failedMessages.update((map) => {
            const state = map.get(key);
            if (state !== undefined) {
                deleted = this.#deleteLocalMessage(state, messageId);
            }
            return map;
        });
        return deleted;
    }
    addEphemeral(key, message) {
        this.#ephemeral.update((map) => {
            const s = map.get(key) ?? new Map();
            s.set(message.event.messageId, message);
            map.set(key, s);
            return map;
        });
        // TODO - I don't think that we want ephemeral messages to automatically disappear
        // but we also don't want them to stay here forever do we?
        // return scheduleUndo(() => {
        //     this.#deleteLocalMessage(this.#ephemeral, key, message.event.messageId);
        // });
    }
    isEphemeral(key, messageId) {
        return this.#ephemeral.value.get(key)?.has(messageId) ?? false;
    }
    get ephemeral() {
        return this.#ephemeral;
    }
    ephemeralMessages(key) {
        const state = this.#ephemeral.value.get(key);
        return state ? [...state.values()] : [];
    }
    #deleteLocalMessage(state, messageId) {
        const msg = state.get(messageId);
        if (msg !== undefined) {
            revokeObjectUrls(msg);
            state.delete(messageId);
            return true;
        }
        return false;
    }
    unconfirmedMessages(key) {
        const state = this.#unconfirmed.value.get(key);
        return state ? [...state.values()] : [];
    }
    addUnconfirmed(key, message) {
        const messageId = message.event.messageId;
        return modifyWritableMap(key, (state) => {
            if (!state.has(messageId)) {
                state.set(messageId, { ...message, accepted: false });
                this.#recentlySentMessages.update((map) => {
                    map.set(messageId, message.timestamp);
                    return map;
                });
                return (state) => {
                    this.#deleteLocalMessage(state, messageId);
                    this.#recentlySentMessages.update((map) => {
                        map.delete(messageId);
                        return map;
                    });
                    return state;
                };
            }
            return (state) => state;
        }, this.#unconfirmed, emptyUnconfirmed, undefined, 90_000);
    }
    get recentlySentMessages() {
        return this.#recentlySentMessages;
    }
    overwriteUnconfirmedContent(key, messageId, content, senderContext, blockLevelMarkdown) {
        this.#unconfirmed.update((map) => {
            const state = map.get(key);
            if (state) {
                const msg = state.get(messageId);
                if (msg) {
                    state.set(messageId, {
                        ...msg,
                        event: {
                            ...msg.event,
                            content,
                            senderContext,
                            blockLevelMarkdown: blockLevelMarkdown ?? false,
                        },
                    });
                    map.set(key, state);
                }
            }
            return map;
        });
    }
    deleteUnconfirmed(key, messageId) {
        let deleted = false;
        if (this.#unconfirmed.value.get(key)?.has(messageId)) {
            this.#unconfirmed.update((map) => {
                const state = map.get(key);
                if (state !== undefined) {
                    deleted = this.#deleteLocalMessage(state, messageId);
                }
                return map;
            });
        }
        return deleted;
    }
    isUnconfirmed(key, messageId) {
        return this.#unconfirmed.value.get(key)?.has(messageId) ?? false;
    }
    isPendingAcceptance(key, messageId) {
        return this.#unconfirmed.value.get(key)?.get(messageId)?.accepted === false;
    }
    markUnconfirmedAccepted(key, messageId) {
        this.#unconfirmed.update((map) => {
            const state = map.get(key);
            if (state !== undefined) {
                const msg = state?.get(messageId);
                if (msg) {
                    msg.accepted = true;
                    map.set(key, state);
                }
            }
            return map;
        });
    }
    // only used for testing
    clearUnconfirmed() {
        this.#unconfirmed.set(new MessageContextMap());
    }
    get groupChatPreviews() {
        return this.#groupChatPreviews;
    }
    get uninitialisedDirectChats() {
        return this.#uninitialisedDirectChats;
    }
    addGroupPreview(chat) {
        // if we recently left the chat it *might* still be in the removed chats local updates
        // so we should remove it from there if it is in there
        modifyWritable((d) => d.undoRemove(chat.id), this.chats, "never");
        return addToWritableMap(chat.id, chat, this.#groupChatPreviews, "never");
    }
    removeGroupPreview(chatId) {
        removeFromWritableMap(chatId, this.#groupChatPreviews, "never");
    }
    addUninitialisedDirectChat(chatId) {
        this.#uninitialisedDirectChats.update((data) => {
            return data.set(chatId, {
                kind: "direct_chat",
                id: chatId,
                them: chatId,
                readByThemUpTo: undefined,
                latestMessage: undefined,
                latestEventIndex: 0,
                latestMessageIndex: undefined,
                lastUpdated: BigInt(Date.now()),
                dateCreated: BigInt(Date.now()),
                metrics: emptyChatMetrics(),
                eventsTTL: undefined,
                eventsTtlLastUpdated: BigInt(0),
                membership: {
                    ...nullMembership(),
                    role: ROLE_OWNER,
                },
            });
        });
        return () => {
            this.#uninitialisedDirectChats.update((data) => {
                data.delete(chatId);
                return data;
            });
        };
    }
    anyUninitialisedDirectChats() {
        return this.#uninitialisedDirectChats.value.size > 0;
    }
    removeUninitialisedDirectChat(chatId) {
        if (this.#uninitialisedDirectChats.value.has(chatId)) {
            this.#uninitialisedDirectChats.update((data) => {
                data.delete(chatId);
                return data;
            });
            return true;
        }
        return false;
    }
    anyCommunityPreviews() {
        return this.previewCommunities.value.size > 0;
    }
    isPreviewingCommunity(id) {
        return this.previewCommunities.value.has(id);
    }
    getPreviewingCommunity(id) {
        return this.previewCommunities.value.get(id);
    }
    addCommunityPreview(val) {
        return addToWritableMap(val.id, val, this.previewCommunities, "never");
    }
    removeChat(chatId) {
        return removeFromWritableLocalMap(chatId, this.chats);
    }
    addChat(chat) {
        this.removeGroupPreview(chat.id);
        return addToWritableLocalMap(chat.id, chat, this.chats);
    }
    removeCommunityPreview(id) {
        return removeFromWritableMap(id, this.previewCommunities, "never");
    }
    addCommunity(val) {
        this.removeCommunityPreview(val.id);
        addToWritableLocalMap(val.id, val, this.communities);
        this.chats.update((map) => {
            val.channels.forEach((c) => map.addOrUpdate(c.id, c));
            return map;
        });
    }
    get messageActivityFeedReadUpTo() {
        return this.#messageActivityFeedReadUpTo;
    }
    setMessageActivityFeedReadUpTo(val) {
        const prev = this.#messageActivityFeedReadUpTo.value;
        this.#messageActivityFeedReadUpTo.set(val);
        return scheduleUndo(() => {
            this.#messageActivityFeedReadUpTo.set(prev);
        });
    }
    get walletConfig() {
        return this.#walletConfig;
    }
    updateWalletConfig(val) {
        const prev = this.#walletConfig.value;
        this.#walletConfig.set(val);
        return scheduleUndo(() => {
            this.#walletConfig.set(prev);
        });
    }
    get streakInsurance() {
        return this.#streakInsurance;
    }
    updateStreakInsurance(val) {
        const prev = this.#streakInsurance.value;
        this.#streakInsurance.set(val);
        return scheduleUndo(() => {
            this.#streakInsurance.set(prev);
        });
    }
    updateCommunityDisplayName(id, name) {
        return this.#modifyCommunitySummaryUpdates(id, (upd) => {
            upd.displayName = name !== undefined ? { value: name } : "set_to_none";
            return (upd) => {
                upd.displayName = undefined;
                return upd;
            };
        }, "updateCommunityDisplayName");
    }
    updateCommunityMember(id, userId, member) {
        return communityLocalUpdates.updateMember(id, userId, member);
    }
    blockCommunityUser(id, userId) {
        return communityLocalUpdates.blockUser(id, userId);
    }
    unblockCommunityUser(id, userId) {
        return communityLocalUpdates.unblockUser(id, userId);
    }
    removeCommunityMember(id, userId) {
        return communityLocalUpdates.removeMember(id, userId);
    }
    inviteCommunityUsers(id, userIds) {
        return communityLocalUpdates.inviteUsers(id, userIds);
    }
    uninviteCommunityUsers(id, userIds) {
        return communityLocalUpdates.uninviteUsers(id, userIds);
    }
    updateCommunityRules(id, rules) {
        return communityLocalUpdates.updateRules(id, rules);
    }
    updateCommunityRulesAccepted(id, accepted) {
        return this.#modifyCommunitySummaryUpdates(id, (upd) => {
            upd.rulesAccepted = accepted;
            return (upd) => {
                upd.rulesAccepted = undefined;
                return upd;
            };
        }, "updateCommunityRulesAccepted");
    }
    deleteUserGroup(id, userGroupId) {
        return communityLocalUpdates.deleteUserGroup(id, userGroupId);
    }
    addOrUpdateUserGroup(id, userGroup) {
        return communityLocalUpdates.addOrUpdateUserGroup(id, userGroup);
    }
    removeBotFromCommunity(id, botId) {
        return communityLocalUpdates.removeBot(id, botId);
    }
    favourite(id) {
        return addToWritableLocalSet(id, this.favourites);
    }
    unfavourite(id) {
        return removeFromWritableLocalSet(id, this.favourites);
    }
    installBotInCommunity(id, botId, perm) {
        return communityLocalUpdates.installBot(id, botId, perm);
    }
    removeCommunity(id) {
        if (this.previewCommunities.value.get(id)) {
            return this.removeCommunityPreview(id);
        }
        else {
            return removeFromWritableLocalMap(id, this.communities);
        }
    }
    updateCommunityIndex(id, index) {
        return this.#modifyCommunitySummaryUpdates(id, (upd) => {
            upd.index = index;
            return (upd) => {
                upd.index = undefined;
                return upd;
            };
        }, "updateCommunityIndex");
    }
    // Chat stuff
    updateChatMember(id, userId, existing, updater) {
        return chatDetailsLocalUpdates.updateMember(id, userId, existing, updater);
    }
    blockChatUser(id, userId) {
        return chatDetailsLocalUpdates.blockUser(id, userId);
    }
    unblockChatUser(id, userId) {
        return chatDetailsLocalUpdates.unblockUser(id, userId);
    }
    removeChatMember(id, userId) {
        return chatDetailsLocalUpdates.removeMember(id, userId);
    }
    addChatMember(id, member) {
        return chatDetailsLocalUpdates.addMember(id, member);
    }
    pinMessage(id, messageIndex) {
        return chatDetailsLocalUpdates.pinMessage(id, messageIndex);
    }
    unpinMessage(id, messageIndex) {
        return chatDetailsLocalUpdates.unpinMessage(id, messageIndex);
    }
    inviteChatUsers(id, userIds) {
        return chatDetailsLocalUpdates.inviteUsers(id, userIds);
    }
    uninviteChatUsers(id, userIds) {
        return chatDetailsLocalUpdates.uninviteUsers(id, userIds);
    }
    updateChatRules(id, rules) {
        return chatDetailsLocalUpdates.updateRules(id, rules);
    }
    removeBotFromChat(id, botId) {
        return chatDetailsLocalUpdates.removeBot(id, botId);
    }
    installBotInChat(id, botId, perm) {
        return chatDetailsLocalUpdates.installBot(id, botId, perm);
    }
    addWebhookToChat(id, webhook) {
        return chatDetailsLocalUpdates.addWebhook(id, webhook);
    }
    updateWebhook(id, webhook) {
        return chatDetailsLocalUpdates.updateWebhook(id, webhook);
    }
    removeWebhookFromChat(id, webhookId) {
        return chatDetailsLocalUpdates.removeWebhook(id, webhookId);
    }
    pinToScope(id, scope) {
        return chatDetailsLocalUpdates.pinToScope(id, scope);
    }
    unpinFromScope(id, scope) {
        return chatDetailsLocalUpdates.unpinFromScope(id, scope);
    }
    removeDirectChatBot(botId) {
        const undo1 = this.removeChat({ kind: "direct_chat", userId: botId });
        const undo2 = removeFromWritableLocalMap(botId, this.directChatBots);
        return () => {
            undo1();
            undo2();
        };
    }
    installDirectChatBot(botId, perm) {
        return addToWritableLocalMap(botId, perm, this.directChatBots);
    }
    #modifyMessageUpdates(messageId, fn, functionName, timeout) {
        return modifyWritableMap(messageId, fn, messageLocalUpdates, () => new MessageLocalUpdates(), `${functionName}_${messageId}`, timeout);
    }
    #modifyCommunitySummaryUpdates(id, fn, functionName) {
        return modifyWritableMap(id, fn, communitySummaryLocalUpdates, () => new CommunitySummaryUpdates(), `${functionName}_${id.communityId}`);
    }
    #modifyChatSummaryUpdates(id, fn, functionName) {
        return modifyWritableMap(id, fn, chatSummaryLocalUpdates, () => new ChatSummaryUpdates(), `${functionName}_${chatIdentifierToString(id)}`);
    }
    updateNotificationsMuted(id, muted) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.notificationsMuted = muted;
            return (upd) => {
                upd.notificationsMuted = undefined;
                return upd;
            };
        }, "updateNotificationsMuted");
    }
    updateArchived(id, archived) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.archived = archived;
            return (upd) => {
                upd.archived = undefined;
                return upd;
            };
        }, "updateArchived");
    }
    updateLatestMessage(id, message) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            if (!dequal(upd.latestMessage, message)) {
                upd.latestMessage = message;
                return (upd) => {
                    if (upd.latestMessage !== undefined) {
                        revokeObjectUrls(upd.latestMessage);
                    }
                    upd.latestMessage = undefined;
                    return upd;
                };
            }
            return (upd) => upd;
        }, "updateLatestMessage");
    }
    updateChatRulesAccepted(id, rulesAccepted) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.rulesAccepted = rulesAccepted;
            return (upd) => {
                upd.rulesAccepted = undefined;
                return upd;
            };
        }, "updateChatRulesAccepted");
    }
    updateDirectChatProperties(id, eventsTTL) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.eventsTTL = eventsTTL;
            return (upd) => {
                upd.eventsTTL = undefined;
                return upd;
            };
        }, "updateDirectChatProperties");
    }
    updateChatProperties(id, name, description, permissions, gateConfig, eventsTTL, isPublic) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.name = name;
            upd.description = description;
            upd.permissions = permissions;
            upd.gateConfig = gateConfig;
            upd.eventsTTL = eventsTTL;
            upd.isPublic = isPublic;
            return (upd) => {
                upd.name = undefined;
                upd.description = undefined;
                upd.permissions = undefined;
                upd.gateConfig = undefined;
                upd.eventsTTL = undefined;
                upd.isPublic = undefined;
                return upd;
            };
        }, "updateChatProperties");
    }
    updateChatFrozen(id, frozen) {
        return this.#modifyChatSummaryUpdates(id, (upd) => {
            upd.frozen = frozen;
            return (upd) => {
                upd.frozen = undefined;
                return upd;
            };
        }, "updateChatFrozen");
    }
    // message updates
    markMessageContentEdited(msg, blockLevelMarkdown) {
        return this.#modifyMessageUpdates(msg.messageId, (upd) => {
            upd.editedContent = msg.content;
            upd.blockLevelMarkdown = blockLevelMarkdown;
            upd.linkRemoved = false;
            return (upd) => {
                upd.editedContent = undefined;
                upd.blockLevelMarkdown = undefined;
                upd.linkRemoved = false;
                return upd;
            };
        }, "markMessageContentEdited");
    }
    markCancelledReminder(messageId, content) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.cancelledReminder = content;
            return (upd) => {
                upd.cancelledReminder = undefined;
                return upd;
            };
        }, "markCancelledReminder");
    }
    markMessageDeleted(messageId, userId) {
        return this.#markMessageDeletedUndeleted(messageId, { deletedBy: userId, timestamp: BigInt(Date.now()) }, undefined, undefined);
    }
    markMessageUndeleted(messageId, content) {
        return this.#markMessageDeletedUndeleted(messageId, undefined, content, undefined);
    }
    markMessageContentRevealed(messageId, content) {
        return this.#markMessageDeletedUndeleted(messageId, undefined, undefined, content);
    }
    markBlockedMessageRevealed(messageId) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.hiddenMessageRevealed = true;
            return (upd) => {
                upd.hiddenMessageRevealed = undefined;
                return upd;
            };
        }, "markBlockedMessageRevealed");
    }
    markLinkRemoved(messageId, content) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.editedContent = content;
            upd.linkRemoved = true;
            return (upd) => {
                upd.editedContent = undefined;
                upd.linkRemoved = false;
                return upd;
            };
        }, "markLinkRemoved");
    }
    markReaction(messageId, reaction) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.reactions.push(reaction);
            return (upd) => {
                upd.reactions = [];
                return upd;
            };
        }, "markReaction");
    }
    markTip(messageId, ledger, userId, amount) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            let map = upd.tips.get(ledger);
            if (map === undefined) {
                map = new Map();
                upd.tips.set(ledger, map);
            }
            const currentAmount = map.get(userId);
            if (currentAmount === undefined) {
                map.set(userId, amount);
            }
            else {
                map.set(userId, currentAmount + amount);
            }
            if ((map.get(userId) ?? 0) <= 0n) {
                map.delete(userId);
            }
            if (map.size === 0) {
                upd.tips.delete(ledger);
            }
            return (upd) => {
                upd.tips = new Map();
                return upd;
            };
        }, "markTip");
    }
    markPrizeClaimed(messageId) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.prizeClaimed = true;
            return (upd) => {
                upd.prizeClaimed = undefined;
                return upd;
            };
        }, "markPrizeClaimed");
    }
    markProposalTallyUpdated(messageId, tally) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.proposalTally = tally;
            return (upd) => {
                upd.proposalTally = undefined;
                return upd;
            };
        }, "markProposalTallyUpdated", 5 * ONE_MINUTE_MILLIS);
    }
    setP2PSwapStatus(messageId, status) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.p2pSwapStatus = status;
            return (upd) => {
                upd.p2pSwapStatus = undefined;
                return upd;
            };
        }, "setP2PSwapStatus");
    }
    markPollVote(messageId, vote) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.pollVotes.push(vote);
            return (upd) => {
                upd.pollVotes = [];
                return upd;
            };
        }, "markPollVote");
    }
    markThreadSummaryUpdated(messageId, summaryUpdates) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.threadSummary = { ...upd.threadSummary, ...summaryUpdates };
            return (upd) => {
                upd.threadSummary = undefined;
                return upd;
            };
        }, "markThreadSummaryUpdated");
    }
    #markMessageDeletedUndeleted(messageId, deleted, undeletedContent, revealedContent) {
        return this.#modifyMessageUpdates(messageId, (upd) => {
            upd.deleted = deleted;
            upd.undeletedContent = undeletedContent;
            upd.revealedContent = revealedContent;
            return (upd) => {
                upd.deleted = undefined;
                upd.undeletedContent = undefined;
                upd.revealedContent = undefined;
                return upd;
            };
        }, "markMessageDeletedUndeleted");
    }
}
export const localUpdates = new GlobalLocalState();
